import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, Star, Clock, MapPin, ChevronRight,
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'Droplets': Droplets,
  'Zap': Zap,
  'Wind': Wind,
  'Sparkles': Sparkles,
  'Hammer': Hammer,
  'Tv': Tv,
  'PaintBucket': PaintBucket,
  'Wrench': Wrench,
};

export default function ServiceDetail() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoryRes, servicesRes] = await Promise.all([
          axios.get(`${API}/categories/${categoryId}`),
          axios.get(`${API}/services?category_id=${categoryId}`)
        ]);
        setCategory(categoryRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [categoryId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-slate-500">Category not found</p>
      </div>
    );
  }

  const IconComponent = iconMap[category.icon] || Wrench;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header with Image */}
      <div className="relative h-64">
        <img 
          src={category.image_url || 'https://images.pexels.com/photos/7347538/pexels-photo-7347538.jpeg'}
          alt={category.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        
        {/* Back Button */}
        <button 
          className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        
        {/* Category Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <IconComponent className="w-7 h-7 text-slate-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-[Poppins]">{category.name}</h1>
              <p className="text-white/80 text-sm">Starting at ₱{category.base_fee}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Description */}
        <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-2">About this service</h2>
          <p className="text-slate-600 text-sm leading-relaxed">{category.description}</p>
        </div>

        {/* Services List */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 font-[Poppins] mb-4">Available Services</h2>
          
          <div className="space-y-3">
            {services.map((service) => (
              <button
                key={service.id}
                className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-left"
                onClick={() => navigate(`/book/${service.id}`)}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{service.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{service.description}</p>
                    
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs">{service.estimated_duration} mins</span>
                      </div>
                      <span className="text-amber-600 font-semibold">₱{service.base_price}</span>
                    </div>
                    
                    {service.add_ons && service.add_ons.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-slate-400">
                          +{service.add_ons.length} add-on{service.add_ons.length > 1 ? 's' : ''} available
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              </button>
            ))}
            
            {services.length === 0 && (
              <div className="text-center py-8">
                <p className="text-slate-500">No services available yet</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
